import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  Divider,
} from '@mui/material'
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material'
import { paymentService, PaymentTransactionCreate } from '../services/paymentService'
import { PaymentMethod } from '../types/payment'
import api from '../services/api'

interface SalesOrder {
  id: string
  order_number: string
  customer_name: string
  total_amount: number
  paid_amount: number
  balance_due: number
  payment_status: string
}

const PaymentRecordPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preselectedOrderId = searchParams.get('orderId')

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [orders, setOrders] = useState<SalesOrder[]>([])
  const [selectedOrder, setSelectedOrder] = useState<SalesOrder | null>(null)
  const [orderBalance, setOrderBalance] = useState<any>(null)

  const [formData, setFormData] = useState<PaymentTransactionCreate>({
    amount: 0,
    payment_method: PaymentMethod.CASH,
    reference_number: '',
    notes: '',
    auto_complete: false,
  })

  // Load orders on mount
  useEffect(() => {
    loadOrders()
  }, [])

  // Preselect order if orderId in URL
  useEffect(() => {
    if (preselectedOrderId && orders.length > 0) {
      const order = orders.find(o => o.id === preselectedOrderId)
      if (order) {
        handleOrderSelect(order)
      }
    }
  }, [preselectedOrderId, orders])

  const loadOrders = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/sales-orders', {
        params: { 
          status: 'confirmed,paid,processing,shipped',
          limit: 100 
        }
      })
      
      // Transform to include balance info
      const ordersData = response.data.items || response.data
      setOrders(ordersData.map((order: any) => ({
        id: order.id,
        order_number: order.order_number,
        customer_name: order.customer?.business_name || order.customer?.contact_name || 'Unknown',
        total_amount: order.total_amount || 0,
        paid_amount: order.paid_amount || 0,
        balance_due: (order.total_amount || 0) - (order.paid_amount || 0),
        payment_status: order.payment_status || 'pending',
      })))
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load orders')
    } finally {
      setLoading(false)
    }
  }

  const handleOrderSelect = async (order: SalesOrder | null) => {
    setSelectedOrder(order)
    setError(null)
    
    if (order) {
      try {
        const balance = await paymentService.getOrderBalance(order.id)
        setOrderBalance(balance)
        
        // Set default amount to remaining balance
        setFormData(prev => ({
          ...prev,
          amount: balance.remaining_balance,
        }))
      } catch (err: any) {
        console.error('Failed to load order balance:', err)
        // Continue anyway with order data
        setFormData(prev => ({
          ...prev,
          amount: order.balance_due,
        }))
      }
    } else {
      setOrderBalance(null)
      setFormData(prev => ({ ...prev, amount: 0 }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedOrder) {
      setError('Please select an order')
      return
    }

    if (formData.amount <= 0) {
      setError('Payment amount must be greater than 0')
      return
    }

    if (formData.amount > (orderBalance?.remaining_balance || selectedOrder.balance_due)) {
      setError('Payment amount cannot exceed balance due')
      return
    }

    try {
      setSaving(true)
      setError(null)
      
      await paymentService.recordPayment(selectedOrder.id, formData)
      
      setSuccess(`Payment of ₹${formData.amount.toFixed(2)} recorded successfully for ${selectedOrder.order_number}`)
      
      // Reset form after 2 seconds
      setTimeout(() => {
        navigate('/admin/documents/sales-orders')
      }, 2000)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to record payment')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    navigate(-1)
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Record Payment
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Record a payment received from customer against a sales order
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Order Selection */}
            <Grid item xs={12}>
              <Autocomplete
                options={orders}
                getOptionLabel={(option) => 
                  `${option.order_number} - ${option.customer_name} (Balance: ₹${option.balance_due.toFixed(2)})`
                }
                value={selectedOrder}
                onChange={(_, newValue) => handleOrderSelect(newValue)}
                loading={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Sales Order *"
                    placeholder="Search by order number or customer name"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Order Details Card */}
            {selectedOrder && orderBalance && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Order Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Order Number
                        </Typography>
                        <Typography variant="body1">
                          {orderBalance.order_number}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Total Amount
                        </Typography>
                        <Typography variant="body1">
                          ₹{orderBalance.total_amount.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Paid Amount
                        </Typography>
                        <Typography variant="body1" color="success.main">
                          ₹{orderBalance.paid_amount.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                          Balance Due
                        </Typography>
                        <Typography variant="body1" color="error.main" fontWeight="bold">
                          ₹{orderBalance.remaining_balance.toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {orderBalance.transaction_count > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="caption" color="text.secondary">
                          Previous Payments: {orderBalance.transaction_count}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Payment Amount */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Amount *"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
                required
                helperText={selectedOrder ? `Maximum: ₹${(orderBalance?.remaining_balance || selectedOrder.balance_due).toFixed(2)}` : ''}
              />
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Payment Method *"
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value as any })}
                required
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="online">Online Payment</MenuItem>
              </TextField>
            </Grid>

            {/* Reference Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference Number"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                placeholder="Cheque no., Transaction ID, etc."
                helperText="Optional: Cheque number, transaction ID, or other reference"
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this payment"
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving || !selectedOrder}
                >
                  {saving ? 'Recording...' : 'Record Payment'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  )
}

export default PaymentRecordPage
